// Story 6.10 (ADO-1164) — My Account profile client.
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// Story 6.7 membership endpoints (`GET`/`PATCH /me/profile`) plus the Story 6.13
// GDPR data-subject actions consumed by the Privacy & Data tab. Mirrors the
// `tenant-context.client.ts` pattern: it is the SINGLE consumer seam until the
// `openapi-typescript`-generated client lands (Story 6.1 deviation D-1), at
// which point these calls move to the generated surface.
//
// The global `/api/v1` prefix landed repo-wide (ADO-1181, closing Story 6.1
// D-1's prefix half); these API_PREFIX paths resolve to live routes.

import { API_PREFIX, apiFetch } from './shoplink-api';

export const PROFILE_PATH = `${API_PREFIX}/me/profile`;
export const GDPR_EXPORT_PATH = `${API_PREFIX}/me/data-export`;
export const GDPR_ERASURE_PATH = `${API_PREFIX}/me/erasure-request`;

/** Wire shape of `GET /me/profile` (Story 6.7 `ProfileResponseDto`). */
export interface Profile {
  readonly userSub: string;
  /** Tenant registry identity; read-only and never accepted by PATCH. */
  readonly fullName?: string;
  readonly displayName: string | null;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly phone: string | null;
  readonly notificationOrderEventsOptedIn: boolean;
  readonly notificationPromoOptedIn: boolean;
  /**
   * OQ-6.8: the Notifications design shows a 3rd toggle (cut-off reminders).
   * Optional here until the Story 6.7/6.12 `profile` column lands; the UI treats
   * `undefined` as "off / not yet available".
   */
  readonly notificationCutoffRemindersOptedIn?: boolean;
  /** From the Auth0 token claim, never persisted. */
  readonly email?: string;
}

/** Partial-update body for `PATCH /me/profile` (Story 6.7 `UpdateProfileDto`). */
export type ProfileUpdate = Partial<
  Pick<
    Profile,
    | 'displayName'
    | 'firstName'
    | 'lastName'
    | 'phone'
    | 'notificationOrderEventsOptedIn'
    | 'notificationPromoOptedIn'
    | 'notificationCutoffRemindersOptedIn'
  >
>;

/** Read the current user's profile. Throws on a non-2xx so the UI can surface it. */
export async function fetchProfile(): Promise<Profile> {
  const res = await apiFetch(PROFILE_PATH);
  if (!res.ok) throw new Error(`Failed to load profile (${res.status})`);
  return (await res.json()) as Profile;
}

/** Persist a partial profile update; returns the updated profile. */
export async function updateProfile(update: ProfileUpdate): Promise<Profile> {
  const res = await apiFetch(PROFILE_PATH, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(update),
  });
  if (!res.ok) throw new Error(`Failed to save profile (${res.status})`);
  return (await res.json()) as Profile;
}

/** A downloadable GDPR export artifact: the file bytes + the server-chosen name. */
export interface DataExportFile {
  readonly blob: Blob;
  readonly filename: string;
}

/**
 * Request the GDPR data export (Story 6.13). The endpoint returns the export
 * artifact itself (200 JSON body + `Content-Disposition: attachment`), so this
 * resolves the downloadable file rather than firing an async "we'll email you"
 * request — the caller triggers the browser download. Throws on a non-2xx.
 */
export async function requestDataExport(): Promise<DataExportFile> {
  const res = await apiFetch(GDPR_EXPORT_PATH, { method: 'POST' });
  if (!res.ok) throw new Error(`Could not start data export (${res.status})`);
  const blob = await res.blob();
  const filename =
    filenameFromContentDisposition(res.headers.get('content-disposition')) ??
    'shoplink-data-export.json';
  return { blob, filename };
}

/**
 * Parse the `filename` from a `Content-Disposition` header, else null. Handles
 * the plain `filename="..."` form the export endpoint sets; ignores the RFC 5987
 * `filename*` form (not emitted here). Never throws.
 */
function filenameFromContentDisposition(header: string | null): string | null {
  if (header === null) return null;
  const match = /filename="?([^"]+)"?/i.exec(header);
  return match?.[1] ?? null;
}

/** Request GDPR account erasure (Story 6.13). Resolves on accepted (202/200). */
export async function requestErasure(): Promise<void> {
  const res = await apiFetch(GDPR_ERASURE_PATH, { method: 'POST' });
  if (!res.ok) throw new Error(`Could not submit erasure request (${res.status})`);
}
